/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Back, Ease} from "gsap";
import {BookOfShadowsGameAssets} from "./BookOfShadowsGameAssets";
import {BookOfShadowsLayerConfig, IBookOfShadowsLayerConfig} from "./config/BookOfShadowsLayerConfig";
import {IBookOfShadowsSymbolStates} from "./symbols/BookOfShadowsReelSymbol";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {BookOfShadowsSoundConfig} from "./BookOfShadowsSoundConfig";
import {ISymbolStateConfig} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";

export interface IBookOfShadowsWinRatioConfig {
    readonly ratio: number;
    readonly text?: string;
    readonly duration: number; // In second.
    readonly type?: WinRatioType;
    readonly sound?: string;
}

export interface IBookOfShadowsWinRatios extends IWinRatios {
    readonly normalWin: IBookOfShadowsWinRatioConfig[];
    readonly bigWin: IBookOfShadowsWinRatioConfig[];
}

export class BookOfShadowsGameConfig implements IGameConfig {

    public static instance: BookOfShadowsGameConfig = new BookOfShadowsGameConfig();

    BROWSER_FPS: number = 30;

    GAME_WIDTH: number = 1280;
    GAME_HEIGHT: number = 720;

    DEFAULT_LAYER_SCALE_OPTION: IDeviceDependentConfig<LayerScaleOption> = BookOfShadowsLayerConfig.defaultLayerScaleOptions;

    LAYERS: IBookOfShadowsLayerConfig = BookOfShadowsLayerConfig.getConfig();

    BET_WIN_MODE: BetWinMode = BetWinMode.BET_LINE;
    SPIN_MODE: SpinMode = SpinMode.SPIN;

    SYMBOL_STATES: IBookOfShadowsSymbolStates = {
        normal: {
            prio: 0,
            keyword: "normal",
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        },
        goldNormal: {
            prio: 0,
            keyword: "Gold",
            texturesCreator: (symName: string, stateKey: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        spin: {
            prio: 1,
            keyword: "spin",
            texturesCreator: (symName: string, stateKey: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        goldSpin: {
            prio: 1,
            keyword: "Gold_spin",
            texturesCreator: (symName: string, stateKey: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        noWin: {
            prio: 2,
            keyword: "noWin",
            texturesCreator: (symName: string, stateKey: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        win: {
            prio: 3,
            keyword: "Win",
            symbols: ["M1", "M1", "M3", "M4", "M5", "S"],
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        },
        goldWin: {
            prio: 3,
            keyword: "Gold_Win",
            symbols: ["M1", "M1", "M3", "M4", "M5"],
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        },
        winToggle: {
            prio: 3,
            keyword: "WinToggle",
            symbols: ["S"],
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        },
        hidden: {
            prio: 4,
            keyword: "hidden"
        },
        idle: {
            prio: 5,
            keyword: "Idle",
            symbols: ["M1", "M1", "M3", "M4", "M5", "S"],
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        },
        land: {
            prio: 6,
            keyword: "Land",
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        },
        goldLand: {
            prio: 6,
            keyword: "Gold_Land",
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        },
        sAnticipation: {
            prio: 7,
            keyword: "Anticipation",
            symbols: ["S"],
            texturesCreator: (symName: string) => {
                return BookOfShadowsGameAssets.getSymbolStateTextures(symName);
            }
        }
    };
    SYMBOL_WIDTH: number = 144;
    SYMBOL_HEIGHT: number = 110;
    SYMBOL_BOTTOM_PADDING: number = 0;

    REELS_NUM: number = 5;
    SPACE_BETWEEN_REELS: number = 0;
    REEL_DISPLAY_POSITIONS: [number, number][] = [
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0]];
    SYMBOLS_NUM_IN_REEL: [number, number, number, number, number] = [0, 1, 5, 1, 0];

    REEL_AREA_POS_X: number = 0;
    REEL_AREA_POS_Y: number = 85;
    REEL_AREA_WIDTH: number = (this.SYMBOL_WIDTH * this.REELS_NUM) + ((this.REELS_NUM - 1) * this.SPACE_BETWEEN_REELS);
    REEL_AREA_HEIGHT: number = (this.SYMBOL_HEIGHT * this.SYMBOLS_NUM_IN_REEL[2]) + ((this.SYMBOLS_NUM_IN_REEL[2] - 1) * this.SYMBOL_BOTTOM_PADDING);

    REEL_SPIN_TIME: number = 0.9;
    REEL_FAST_SPIN_SPIN_TIME: number = 0.2;

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION: number = 0.2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY: number = 0.3;
    REEL_SPIN_START_EASE: Ease = Back.easeIn.config(4);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION: number = 0.05;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY: number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG: [number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG: [number, number] = [1.2, 0.75];

    REEL_SPIN_START_SPEED: number = 0.3;
    REEL_SPIN_SPIN_SPEED: number = 0.04;
    REEL_NEAR_WIN_SPIN_SPEED: number = 0.03;
    REEL_SPIN_STOP_SPEED: number = 0.5;
    REEL_SPIN_QUICK_STOP_SPEED: number = 0.3;

    REEL_STOP_DELAY: number = 0.3;
    REEL_QUICK_STOP_DELAY: number = 0.05;
    REEL_NEAR_WIN_STOP_DELAY: number = 0.0; // it is controlled from GameModuleConfig's NearWin in game round sequence.
    REEL_SETS: IReelSets = {
        "BASE_REELSET": [
            ["M2", "M5", "L3", "L5", "M1", "M3", "L4", "L1", "M2", "M4", "L3", "L2", "L1", "M3", "M2", "L3", "M1", "L4", "M4", "M2", "L3", "M1", "L5", "L4", "M2", "L3", "M3", "M1", "L1", "M2", "L4", "M5", "L5", "L3", "L2", "L4", "M3", "M1", "L5", "L2", "L1", "M5", "M2", "M4", "L2", "M1", "M5", "L1", "L4", "L2", "M1", "M5", "L5", "S", "M4", "L1", "M1", "L5", "M5", "L2", "L1", "L4", "M1", "L3", "L5", "L2"],
            ["M1", "L1", "M2", "M3", "L2", "M4", "L1", "M1", "M5", "L2", "M2", "L5", "L3", "M1", "L2", "L4", "L5", "M5", "L1", "M3", "L2", "M1", "L3", "L1", "L4", "M3", "M4", "L3", "M1", "L2", "L5", "L1", "M3", "M5", "L4", "L5", "S", "L1", "M5", "M2", "L2", "L3", "M1", "L4", "L1", "M4", "L3", "L2", "M5", "L1", "L5", "L3", "L2", "L4", "L1", "L5", "L3", "L2", "M1", "L1", "L4", "L3", "L2", "M1", "L5", "L4", "M5", "L2"],
            ["M3", "M1", "L1", "M4", "L4", "L5", "M5", "M1", "M3", "M4", "L5", "M5", "L4", "M3", "L3", "M4", "L1", "L5", "M5", "L3", "L4", "M2", "L5", "M3", "L3", "L4", "M5", "L2", "M2", "M4", "M3", "L3", "L1", "L5", "L4", "M4", "M2", "M5", "L2", "L4", "L3", "M2", "L1", "M4", "M3", "M1", "S", "L1", "L2", "M5", "M3", "L5", "L1", "L2", "L3", "L4", "L5", "M1", "L2", "L1", "M4", "L5", "M5", "L2", "L3", "L4"],
            ["M3", "M1", "M2", "L5", "L1", "M3", "M4", "M2", "L2", "L1", "M3", "L5", "L4", "M4", "M2", "M1", "M3", "L3", "L2", "L5", "M5", "M2", "L1", "L4", "L2", "L5", "M3", "L1", "M5", "L2", "L5", "M3", "L1", "M1", "L2", "L5", "L3", "S", "M3", "L1", "L5", "L3", "M2", "L2", "M1", "M5", "L3", "L5", "L1", "L2", "M2", "L3", "L4", "L5", "L1", "M5", "M2", "L2", "L5", "M1", "M5", "M4", "M2", "L4", "L5", "L3", "M5"],
            ["M4", "M5", "M3", "L1", "L4", "M4", "L3", "L2", "L1", "L5", "M4", "L3", "M5", "L1", "L2", "M4", "L5", "S", "L4", "L1", "M4", "L5", "L2", "M1", "M2", "L1", "L5", "M5", "L2", "M2", "L3", "M4", "M5", "L2", "M1", "M2", "L4", "M5", "S", "M3", "M2", "L4", "M5", "L1", "L3", "M2", "L5", "M5", "M3", "L4", "L3", "L5", "M1", "L1", "L4", "M4", "M5", "L2", "L3", "L4", "L5", "M2"]
        ],
        "FREESPIN_REELSET": [
            ["M1", "M5", "L3", "L4", "L5", "M1", "M2", "L2", "L4", "M3", "M1", "L3", "M4", "L4", "M2", "L2", "M3", "L3", "L4", "M2", "L5", "M4", "L1", "M1", "M2", "L5", "L3", "L1", "M3", "L2", "L4", "M1", "M5", "L5", "M4", "M3", "L2", "L3", "L5", "L1", "L2", "M5", "L3", "L5", "L2", "M4", "M5", "L5", "L4", "L1", "L2", "M5", "L5", "S", "M1", "M4", "L3", "L5", "M5", "M1", "L2", "L3", "L4", "L5"],
            ["M1", "M2", "L2", "L3", "L1", "M3", "M2", "L2", "M4", "M5", "L3", "L5", "M3", "L1", "M4", "L4", "L2", "M2", "L3", "M3", "L5", "L4", "L1", "M1", "L2", "M5", "L5", "M3", "L1", "M1", "M5", "L5", "L1", "S", "L2", "M5", "M1", "L3", "L1", "L5", "L2", "L4", "L3", "M5", "L1", "M4", "L2", "L3", "L5", "M5", "L1", "L4", "L2", "L3", "S", "L5", "L4", "L2", "M1", "L5", "L3", "L2", "L4"],
            ["M3", "L5", "L1", "M2", "L4", "M4", "M5", "L5", "M3", "M2", "M1", "L4", "M5", "M3", "L3", "L5", "L1", "M5", "L4", "L5", "L1", "L2", "M2", "M4", "M5", "M3", "L3", "L1", "M4", "M1", "M3", "L3", "L5", "M4", "M3", "L4", "L2", "M2", "M5", "L3", "L4", "M3", "L2", "M5", "L1", "M4", "M1", "S", "L1", "L2", "M4", "M3", "L1", "L3", "M1", "L5", "L4", "L2"],
            ["M3", "L1", "M2", "L5", "M5", "L3", "M4", "L1", "L5", "M3", "M5", "M4", "L4", "M1", "M3", "M2", "M4", "L2", "L3", "L5", "M5", "M1", "L2", "L4", "L1", "M5", "M3", "L5", "L1", "L2", "L4", "M3", "L1", "L5", "M1", "L2", "M3", "L3", "S", "L1", "L5", "L3", "M2", "L1", "M1", "M3", "L2", "L3", "L5", "M2", "L4", "L1", "L3", "M5", "M2", "L2", "L5", "L3"],
            ["M4", "M5", "M2", "L1", "M3", "M4", "L4", "L3", "L5", "M3", "M4", "L2", "L4", "M1", "M3", "L3", "M2", "L5", "M4", "L2", "L4", "L3", "L1", "M5", "L2", "L5", "L3", "L1", "M2", "M5", "M4", "L3", "M2", "M5", "L5", "L4", "M2", "M3", "S", "L2", "L5", "M5", "L4", "M1", "M4", "M2", "L5", "M5", "L4", "L1", "M3", "S", "L5", "M4", "L4", "M3", "M1", "L5"]
        ]
    };
    WIN_RATIOS: IBookOfShadowsWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0,
                type: WinRatioType.TINY,
                sound: BookOfShadowsSoundConfig.TINY_WIN
            },
            {
                ratio: 1.5,
                text: "Small Win",
                duration: 0.671,
                type: WinRatioType.SMALL,
                sound: BookOfShadowsSoundConfig.SMALL_WIN
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 1.159,
                type: WinRatioType.MEDIUM,
                sound: BookOfShadowsSoundConfig.MEDIUM_WIN
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 1.523,
                type: WinRatioType.LARGE,
                sound: BookOfShadowsSoundConfig.LARGE_WIN
            }
        ],
        bigWin: [
            {
                ratio: 15,
                text: "Big Win",
                duration: 4.5,
                type: WinRatioType.BIG_WIN
            },
            {
                ratio: 25,
                text: "Mega Win",
                duration: 9.5,
                type: WinRatioType.MEGA_WIN
            },
            {
                ratio: 50,
                text: "Super Mega Win",
                duration: 14,
                type: WinRatioType.SUPER_MEGA_WIN
            }
        ]
    };
    PERFORMANCE_LOGGER: boolean = true;
    // Global variable to check the active rows when spin button is pressed
    ACTIVE_ROWS: number = 3; // defaulting it to 3 since we always have 3 rows(active symbols in a reel)
    ACTIVE_ROWS_ALLOWED_JURISDICTION: boolean = true;
    // Global variable to be used in ReelPartAnimator
    FS_SPECIAL_SYMBOL: string = "";
    // Global variable to signal click of redraw button on FS intro
    REDRAW_ON_FS_CLICKED: boolean = false;
    // Global variable for locked reels
    LOCKED_REEL: string[] = ["0", "0", "0", "0", "0"];
    LUCKY_LOCKS_ALLOWED_VIA_BET: boolean = true;
    LUCKY_LOCKS_ALLOWED_JURISDICTION: boolean = true;
    // Global variable for scatter count for sound
    SCATTER_COUNT: number = 0;
    SCATTER_REEL_ID: boolean[] = [false, false, false, false, false];
    SCATTER_REEL_SYM_ID: number[] = [-1, -1, -1, -1, -1];
    // Global variable for anticipation sound
    ANTICIPATION_SOUND_PLAYED: boolean = false;
    // Global variable for FS special symbol sound
    FS_SPACIAL_SYMBOL_SOUND_PLAYED: boolean = false;
    // Global variable to use delay in abort before FS intro or FS outro
    ABORT_DELAY: number = 0;

    // #331
    TOTAL_COST: number = 0;
    WIN_BELOW_STAKE: boolean = false;

    private constructor() {
        if (BookOfShadowsGameConfig.instance) {
            throw new Error("BookOfShadowsGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }

    REEL_SPIN_STOP_SYMBOL_STATE: () => ISymbolStateConfig = () => {
        return BookOfShadowsGameConfig.instance.SYMBOL_STATES.land;
    };
}